import { z } from 'zod';
import {
  billingCycleSchema,
  planKeySchema,
  planModuleSchema,
  subscriptionStatusSchema,
  type PlanRefusal,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../api/errors.js';

/**
 * Reading a plan refusal off a rejected request.
 *
 * The API answers a limit with a structured payload in `details` — the limit that was hit,
 * the plan they are on, the plan that would fix it and what that costs. Parsing it rather
 * than pattern-matching the prose is what lets the prompt name the specific thing they need:
 * a generic "upgrade required" wastes the single moment somebody is most willing to pay.
 *
 * It is parsed with a schema rather than cast, because `details` is `Record<string, unknown>`
 * on the wire and a missing field would otherwise render as `undefined` inside a sentence
 * about money.
 */

const planRefusalSchema = z.object({
  code: z.enum(['plan_limit_reached', 'module_not_in_plan', 'subscription_read_only']),
  limit: z.enum(['staff_seats', 'module', 'subscription']),
  currentPlan: planKeySchema,
  requiredPlan: planKeySchema.nullable(),
  requiredPlanLabel: z.string().nullable(),
  requiredPlanMonthlyPricePaise: z.number().int().nonnegative().nullable(),
  module: planModuleSchema.optional(),
  used: z.number().int().nonnegative().optional(),
  allowance: z.number().int().nonnegative().optional(),
  subscriptionStatus: subscriptionStatusSchema.optional(),
});

/** The refusal an error carries, or `null` when it is an ordinary failure. */
export function planRefusalFrom(error: unknown): PlanRefusal | null {
  if (!isApiRequestError(error)) return null;
  const parsed = planRefusalSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

/** True when this failure is a plan limit rather than a bug or a bad input. */
export function isPlanRefusal(error: unknown): boolean {
  return planRefusalFrom(error) !== null;
}

/**
 * A blocked plan CHANGE, which is a different shape: it is not "buy more", it is "change
 * something first", and it names how many people or invitations are in the way.
 */
const planChangeBlockSchema = z.object({
  limit: z.literal('staff_seats'),
  reason: z.enum(['staff_over_allowance', 'invites_over_allowance']),
  targetPlan: planKeySchema,
  used: z.number().int().nonnegative(),
  allowance: z.number().int().nonnegative(),
  excess: z.number().int().nonnegative(),
});
export type PlanChangeBlock = z.infer<typeof planChangeBlockSchema>;

export function planChangeBlockFrom(error: unknown): PlanChangeBlock | null {
  if (!isApiRequestError(error)) return null;
  const parsed = planChangeBlockSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

/** Re-exported so a caller needs one import to build a request. */
export { billingCycleSchema };
